import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box, Checkbox, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  IconButton, ListItemText, Menu, MenuItem, Select, Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SendIcon from "@mui/icons-material/Send";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import i18next from "i18next";
import { BRAND, FONT, RADIUS, SHADOW, useLightStyles, MEDIA } from "./lightTheme";
import { Context } from "states/Store";
import { RoleBasedAccessControlContext, PrivilegeEnum, cleanRoleHierarchy, getSortedFlatRoles } from "services/common/RolesUtils";
import { getAccountUsers, createAccountUsers, deleteAccountUsers, reinviteAccountUsers, getUserRoles, updateUserRole } from "services/edge/UserService";
import { getAccountRoles } from "services/edge/RoleService";
import { useAuth } from "@ea-webapp/auth";
import { ERROR, SUCCESS, getUserLanguages, REGEXP_EMAIL } from "services/common/Constants";
import { getApiErrorMessageCode } from "services/common/ApiUtils";
import StopConfirmDialog from "./components/StopConfirmDialog";

// ─── Status config ──────────────────────────────────────────────────────────────
const USER_STATUS = {
  REGISTERED:         { label: () => i18next.t("light.account.statusRegistered"), bg: "#DCFCE7", color: "#15803D" },
  INSCRIT:            { label: () => i18next.t("light.account.statusRegistered"), bg: "#DCFCE7", color: "#15803D" },
  INVITED:            { label: () => i18next.t("light.account.statusInvited"),    bg: "#DBEAFE", color: "#1D4ED8" },
  INVITE:             { label: () => i18next.t("light.account.statusInvited"),    bg: "#DBEAFE", color: "#1D4ED8" },
  EXPIRED_INVITATION: { label: () => i18next.t("light.account.statusExpired"),    bg: "#FEE2E2", color: "#B91C1C" },
};

// ─── Styles ─────────────────────────────────────────────────────────────────────
const useStyles = makeStyles()(() => ({
  card: {
    backgroundColor: BRAND.white,
    borderRadius: RADIUS.lg,
    border: `1.5px solid ${BRAND.border}`,
    boxShadow: SHADOW.soft,
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "1rem 1.25rem",
    borderBottom: `1px solid ${BRAND.border}`,
    gap: "0.75rem",
    [MEDIA.smDown]: {
      flexDirection: "column", alignItems: "stretch",
      padding: "0.85rem 1rem", gap: "0.65rem",
    },
  },
  headerActions: {
    display: "flex", alignItems: "center", gap: "0.5rem",
    [MEDIA.smDown]: { width: "100%" },
  },
  headerSearch: {
    width: "200px", padding: "0.35rem 0.65rem", fontSize: "0.82rem",
    [MEDIA.smDown]: { width: "auto", flex: 1, minWidth: 0 },
  },
  headerTitle: {
    ...FONT.h3, color: BRAND.textMain, fontFamily: FONT.family,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.85rem 1.25rem",
    borderBottom: `1px solid ${BRAND.border}`,
    transition: "background 0.1s",
    "&:last-child": { borderBottom: "none" },
    "&:hover": { backgroundColor: BRAND.background },
    [MEDIA.smDown]: {
      flexWrap: "wrap",
      rowGap: "0.5rem",
      padding: "0.75rem 1rem",
    },
  },
  avatar: {
    width: "34px", height: "34px", borderRadius: "50%",
    backgroundColor: `${BRAND.primary}15`,
    color: BRAND.primary,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "0.72rem", fontWeight: 700, fontFamily: FONT.family,
    flexShrink: 0,
  },
  userInfo: {
    flex: 1, minWidth: 0,
    [MEDIA.smDown]: {
      flex: "1 1 calc(100% - 34px - 34px - 1.5rem)",
    },
  },
  userName: {
    ...FONT.body, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  userEmail: {
    ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  chip: {
    display: "inline-flex", alignItems: "center",
    borderRadius: "20px", padding: "3px 10px",
    fontSize: "0.72rem", fontWeight: 600, fontFamily: FONT.family,
    flexShrink: 0, whiteSpace: "nowrap",
    [MEDIA.smDown]: {
      order: 1,
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  roleChip: {
    backgroundColor: BRAND.background, color: BRAND.textMuted,
    border: `1px solid ${BRAND.border}`,
  },
  actionBtn: {
    background: "none", border: "none", cursor: "pointer",
    color: BRAND.textMuted, padding: "4px", borderRadius: "6px",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "color 0.15s, background 0.15s",
    "&:hover": { color: BRAND.primary, backgroundColor: BRAND.background },
  },
  empty: {
    textAlign: "center", padding: "2.5rem 1rem",
    ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family,
  },
  centered: {
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    gap: "0.75rem", padding: "2.5rem",
    ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family,
  },
  // Dialog form
  formRow: {
    display: "flex", gap: "0.75rem", alignItems: "flex-start", flexWrap: "wrap", marginBottom: "1rem",
  },
  formField: {
    flex: 1, minWidth: "180px", display: "flex", flexDirection: "column", gap: "0.3rem",
  },
  formLabel: {
    ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family,
  },
  roleSelect: {
    fontFamily: FONT.family,
    fontSize: "0.85rem",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BRAND.border },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BRAND.primary },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BRAND.primary },
  },
  roleChips: {
    display: "flex", flexWrap: "wrap", gap: "4px",
  },
}));

// ─── RoleMultiSelect ─────────────────────────────────────────────────────────────
function RoleMultiSelect({ roles, value, onChange, className }) {
  const { classes: s } = useStyles();
  return (
    <Select
      multiple
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${s.roleSelect} ${className || ""}`}
      size="small"
      fullWidth
      renderValue={(selected) => (
        <Box className={s.roleChips}>
          {selected.map((id) => {
            const r = roles.find((r) => r.value === id);
            return r ? (
              <Chip
                key={id}
                label={r.label}
                size="small"
                onMouseDown={(e) => e.stopPropagation()}
                onDelete={() => onChange(value.filter((v) => v !== id))}
                sx={{ fontFamily: FONT.family, fontSize: "0.72rem", height: "22px" }}
              />
            ) : null;
          })}
        </Box>
      )}
      MenuProps={{ PaperProps: { sx: { maxHeight: 280, fontFamily: FONT.family } } }}
    >
      {roles.map((r) => (
        <MenuItem key={r.value} value={r.value} dense sx={{ fontFamily: FONT.family, fontSize: "0.85rem" }}>
          <Checkbox checked={value.includes(r.value)} size="small" sx={{ color: BRAND.primary, "&.Mui-checked": { color: BRAND.primary }, p: "2px", mr: "6px" }} />
          <ListItemText primary={r.label} primaryTypographyProps={{ sx: { fontFamily: FONT.family, fontSize: "0.85rem" } }} />
        </MenuItem>
      ))}
    </Select>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────────
export default function LightUsers() {
  const { classes: s, cx } = useStyles();
  const { classes: light } = useLightStyles();
  const [state, dispatch] = useContext(Context);
  const { hasAnyOfPrivileges, isGlobalAdmin } = useContext(RoleBasedAccessControlContext);
  const { authState } = useAuth();

  const canCreate = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_USER);
  const canEdit   = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_USER);
  const canDelete = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_DELETE_USER);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState("");

  // Invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLang, setInviteLang] = useState(i18next.language || "fr");
  const [inviteRoleIds, setInviteRoleIds] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState("");

  // Edit dialog
  const [editTarget, setEditTarget] = useState(null);
  const [editRoleIds, setEditRoleIds] = useState([]);
  const [editLoading, setEditLoading] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Action menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuUser, setMenuUser] = useState(null);

  const accountId = state.account?.id;

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        getAccountUsers(accountId, dispatch),
        isGlobalAdmin
          ? getAccountRoles(accountId, true, false, false, dispatch)
          : getUserRoles(accountId, authState.user?.id, true, false, false, dispatch),
      ]);
      setUsers(usersRes?.content ?? []);

      const raw = rolesRes?.content ?? [];
      let filtered = isGlobalAdmin
        ? (() => { const def = raw.find((r) => r.default); return def ? [def] : []; })()
        : cleanRoleHierarchy(raw);
      const flat = getSortedFlatRoles(filtered).map((r) => ({ value: r.id, label: r.name }));
      setRoles(flat);
      if (flat.length > 0) setInviteRoleIds([flat[0].value]);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, [accountId, isGlobalAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // ── Invite ──
  const handleInvite = async () => {
    const trimmed = inviteEmail.trim();
    if (!trimmed) return;
    if (!REGEXP_EMAIL.test(trimmed)) { setInviteError(i18next.t("light.profile.invalidEmail")); return; }
    setInviteError("");
    setInviteLoading(true);
    try {
      await createAccountUsers(accountId, {
        email: trimmed,
        lang: inviteLang,
        roleIds: inviteRoleIds,
      }, dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.account.inviteSuccess") } });
      setInviteOpen(false);
      setInviteEmail("");
      setInviteError("");
      await load();
    } catch (e) {
      const errMsg = e?.message?.messages?.error?.[0]?.message;
      const errCode = e?.message?.messages?.error?.[0]?.code;
      if (errMsg?.includes("Blacklisted")) {
        setInviteError(i18next.t("infos.EMAIL_BLACKLISTED_ERROR"));
      } else if (errMsg?.includes("Invalid email")) {
        setInviteError(i18next.t("light.profile.invalidEmail"));
      } else if (errCode === "E133") {
        const details = errMsg?.match(/\(([^)]+)\)/)?.[1] ?? "";
        setInviteError(i18next.t("invalidEmail", { details }));
      } else {
        setInviteError(getApiErrorMessageCode(e));
      }
    } finally {
      setInviteLoading(false);
    }
  };

  // ── Edit ──
  const openEdit = (user) => {
    // pre-select current roles by matching label against roles list
    const userRoles = Array.isArray(user.roles) ? user.roles : user.roles ? [user.roles] : [];
    const currentIds = roles.filter((r) => userRoles.includes(r.label)).map((r) => r.value);
    setEditRoleIds(currentIds.length > 0 ? currentIds : roles.length > 0 ? [roles[0].value] : []);
    setEditTarget(user);
  };

  const handleEdit = async () => {
    if (!editTarget || editRoleIds.length === 0) return;
    setEditLoading(true);
    try {
      await updateUserRole(accountId, editTarget.id, { roleIds: editRoleIds }, dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.account.editSuccess") } });
      setEditTarget(null);
      await load();
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setEditLoading(false);
    }
  };

  // ── Reinvite ──
  const handleReinvite = async (user) => {
    try {
      await reinviteAccountUsers(accountId, user.id, dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.account.reinviteSuccess") } });
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAccountUsers(accountId, [deleteTarget.id], dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.account.deleteSuccess") } });
      setDeleteTarget(null);
      setDeleteReason("");
      await load();
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setDeleting(false);
    }
  };

  const openMenu = (e, user) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setMenuUser(user); };
  const closeMenu = () => { setMenuAnchor(null); setMenuUser(null); };

  const q = search.toLowerCase().trim();
  const filteredUsers = q
    ? users.filter((u) => [u.firstname, u.name, u.email, ...(Array.isArray(u.roles) ? u.roles : [u.roles])].filter(Boolean).join(" ").toLowerCase().includes(q))
    : users;

  const hasActions = canCreate || canEdit || canDelete;

  return (
    <>
      <Box className={s.card}>
        {/* Header */}
        <Box className={s.header}>
          <Typography className={s.headerTitle}>
            {i18next.t("light.account.menuUsers")}
            {!loading && <span style={{ ...FONT.small, color: BRAND.textMuted, fontWeight: 400, marginLeft: "0.5rem" }}>({users.length})</span>}
          </Typography>
          <Box className={s.headerActions}>
            <input
              type="text"
              placeholder={i18next.t("search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={cx(light.fieldInput, s.headerSearch)}
            />
            {canCreate && (
              <button className={light.btnPrimary} style={{ padding: "0.4rem 0.85rem", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: "0.3rem", whiteSpace: "nowrap", flexShrink: 0 }} onClick={() => setInviteOpen(true)}>
                <PersonAddOutlinedIcon style={{ fontSize: "15px" }} />
                {i18next.t("light.account.invite")}
              </button>
            )}
          </Box>
        </Box>

        {/* List */}
        {loading ? (
          <Box className={s.centered}>
            <CircularProgress size={24} style={{ color: BRAND.primary }} />
            <span>{i18next.t("light.account.loadingUsers")}</span>
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box className={s.empty}>{search ? i18next.t("light.common.noResult") : i18next.t("light.account.noUsers")}</Box>
        ) : (
          filteredUsers.map((user) => {
            const initials = [user.firstname?.[0], user.name?.[0]].filter(Boolean).join("").toUpperCase() || "?";
            const fullName = [user.firstname, user.name].filter(Boolean).join(" ") || user.email;
            const stCfg = USER_STATUS[user.status] ?? { label: () => user.status, bg: BRAND.background, color: BRAND.textMuted };
            const roleNames = Array.isArray(user.roles)
              ? user.roles
              : user.roles ? [user.roles] : [];

            return (
              <Box key={user.id} className={s.row}>
                <Box className={s.avatar}>{initials}</Box>
                <Box className={s.userInfo}>
                  <Typography className={s.userName}>{fullName}</Typography>
                  <Typography className={s.userEmail}>{user.email}</Typography>
                </Box>
                {roleNames.map((r) => (
                  <Box key={r} className={cx(s.chip, s.roleChip)}>{r}</Box>
                ))}
                <Box className={s.chip} style={{ backgroundColor: stCfg.bg, color: stCfg.color }}>{stCfg.label()}</Box>
                {hasActions && (
                  <button className={s.actionBtn} onClick={(e) => openMenu(e, user)}>
                    <MoreVertIcon style={{ fontSize: "18px" }} />
                  </button>
                )}
              </Box>
            );
          })
        )}
      </Box>

      {/* Action menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        slotProps={{ paper: { sx: { borderRadius: RADIUS.md, border: `1px solid ${BRAND.border}`, boxShadow: SHADOW.soft, minWidth: 160 } } }}
      >
        {menuUser && !(menuUser.status === "REGISTERED" || menuUser.status === "INSCRIT") && canCreate && (
          <MenuItem onClick={() => { handleReinvite(menuUser); closeMenu(); }} sx={{ fontFamily: FONT.family, fontSize: "0.85rem", gap: "0.5rem" }}>
            <SendIcon style={{ fontSize: "15px" }} /> {i18next.t("light.account.reinvite")}
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem onClick={() => { openEdit(menuUser); closeMenu(); }} sx={{ fontFamily: FONT.family, fontSize: "0.85rem", gap: "0.5rem" }}>
            <EditOutlinedIcon style={{ fontSize: "15px" }} /> {i18next.t("light.account.edit")}
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={() => { setDeleteTarget(menuUser); closeMenu(); }} sx={{ fontFamily: FONT.family, fontSize: "0.85rem", gap: "0.5rem", color: "#DC2626" }}>
            <DeleteOutlineIcon style={{ fontSize: "15px" }} /> {i18next.t("light.account.delete")}
          </MenuItem>
        )}
      </Menu>

      {/* Delete confirmation */}
      <StopConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => { setDeleteTarget(null); setDeleteReason(""); }}
        onConfirm={handleDelete}
        loading={deleting}
        title={i18next.t("light.account.delete")}
        reasonLabel={i18next.t("light.detail.stopReasonLabel")}
        reasonPlaceholder=""
        reason={deleteReason}
        onReasonChange={setDeleteReason}
        cancelLabel={i18next.t("cancel")}
        confirmLabel={i18next.t("light.account.delete")}
        confirmingLabel={i18next.t("light.detail.deleting")}
      >
        {i18next.t("light.account.deleteConfirm", { name: [deleteTarget?.firstname, deleteTarget?.name].filter(Boolean).join(" ") || deleteTarget?.email })}
      </StopConfirmDialog>

      {/* Edit dialog */}
      <Dialog
        open={Boolean(editTarget)}
        onClose={() => { if (!editLoading) setEditTarget(null); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: RADIUS.lg, fontFamily: FONT.family } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "Inter", fontSize: "1rem", fontWeight: 700, color: BRAND.white, backgroundColor: BRAND.primary, pb: 1.5 }}>
          <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <EditOutlinedIcon style={{ fontSize: "18px" }} />
            {i18next.t("light.account.editUser")}
          </Box>
          <IconButton size="small" onClick={() => { if (!editLoading) setEditTarget(null); }} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          {editTarget && (
            <>
              <Typography style={{ ...FONT.body, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family, marginBottom: "0.25rem" }}>
                {[editTarget.firstname, editTarget.name].filter(Boolean).join(" ") || editTarget.email}
              </Typography>
              <Typography style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "1.25rem" }}>
                {editTarget.email}
              </Typography>
            </>
          )}
          <Box className={s.formField} style={{ marginBottom: "1.5rem" }}>
            <Typography className={s.formLabel}>{i18next.t("light.account.roleLabel")}</Typography>
            <RoleMultiSelect roles={roles} value={editRoleIds} onChange={setEditRoleIds} />
          </Box>
          <Box style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
            <button className={light.btnSecondary} style={{ padding: "0.5rem 1.25rem" }} onClick={() => setEditTarget(null)} disabled={editLoading}>
              {i18next.t("cancel")}
            </button>
            <button className={light.btnPrimary} style={{ padding: "0.5rem 1.25rem" }} onClick={handleEdit} disabled={editRoleIds.length === 0 || editLoading}>
              {editLoading ? i18next.t("light.detail.saving") : i18next.t("save")}
            </button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Invite dialog */}
      <Dialog
        open={inviteOpen}
        onClose={() => { if (!inviteLoading) { setInviteOpen(false); setInviteError(""); } }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: RADIUS.lg, fontFamily: FONT.family } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "Inter", fontSize: "1rem", fontWeight: 700, color: BRAND.white, backgroundColor: BRAND.primary, pb: 1.5 }}>
          <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <PersonAddOutlinedIcon style={{ fontSize: "18px" }} />
            {i18next.t("light.account.inviteUser")}
          </Box>
          <IconButton size="small" onClick={() => { if (!inviteLoading) { setInviteOpen(false); setInviteError(""); } }} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          <Box className={s.formRow}>
            <Box className={s.formField}>
              <Typography className={s.formLabel}>{i18next.t("light.account.email")}</Typography>
              <input
                type="email"
                placeholder="nom@entreprise.com"
                value={inviteEmail}
                onChange={(e) => { setInviteEmail(e.target.value); if (inviteError) setInviteError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleInvite()}
                className={light.fieldInput}
                style={{ width: "100%", boxSizing: "border-box", borderColor: inviteError ? "#DC2626" : undefined }}
                autoFocus
              />
            </Box>
            <Box className={s.formField} style={{ flex: "0 1 140px", minWidth: "120px" }}>
              <Typography className={s.formLabel}>{i18next.t("light.account.language")}</Typography>
              <select
                value={inviteLang}
                onChange={(e) => setInviteLang(e.target.value)}
                className={light.fieldInput}
                style={{ width: "100%", boxSizing: "border-box", cursor: "pointer" }}
              >
                {getUserLanguages().map((l) => (
                  <option key={l.code} value={l.code}>{l.name}</option>
                ))}
              </select>
            </Box>
          </Box>
          {roles.length > 0 && (
            <Box className={s.formRow}>
              <Box className={s.formField}>
                <Typography className={s.formLabel}>{i18next.t("light.account.roleLabel")}</Typography>
                <RoleMultiSelect roles={roles} value={inviteRoleIds} onChange={setInviteRoleIds} />
              </Box>
            </Box>
          )}
          {inviteError && (
            <Typography style={{ ...FONT.small, color: "#DC2626", fontFamily: FONT.family, marginBottom: "0.75rem" }}>{inviteError}</Typography>
          )}
          <Box style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", paddingTop: "0.5rem" }}>
            <button className={light.btnSecondary} style={{ padding: "0.5rem 1.25rem" }} onClick={() => { setInviteOpen(false); setInviteError(""); }} disabled={inviteLoading}>
              {i18next.t("cancel")}
            </button>
            <button className={light.btnPrimary} style={{ padding: "0.5rem 1.25rem" }} onClick={handleInvite} disabled={!inviteEmail.trim() || inviteRoleIds.length === 0 || inviteLoading}>
              {inviteLoading ? i18next.t("light.detail.sending") : i18next.t("light.account.invite")}
            </button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
